<template>
  <div class="ap-page">
    <v-snackbar v-model="showSnackBar" top right :timeout="4500">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <vue-element-loading :active="busy" :is-full-screen="true" background-color="rgba(255,255,255,0.7)" color="#16a34a" spinner="spinner" />

    <div v-if="loading" class="ap-card pa-6"><v-skeleton-loader type="article, article"></v-skeleton-loader></div>

    <div v-else-if="!app" class="ap-card">
      <div class="ap-empty">
        <span class="ap-empty-icon"><v-icon size="34">mdi-file-alert-outline</v-icon></span>
        <div class="ap-empty-title">Application not found</div>
        <button type="button" class="ap-btn ap-btn--outline" @click="$router.push('/mybooking')">Back to my applications</button>
      </div>
    </div>

    <template v-else>
      <!-- Hero -->
      <section class="ap-hero detail-hero">
        <div class="ap-hero-copy">
          <span class="ap-kicker">Application No. {{ app.applicationNo }}</span>
          <h1 class="ap-hero-title">{{ filmName }}</h1>
          <p class="ap-hero-sub">{{ nextStepText(app) }}</p>
          <div class="ap-hero-meta">
            <span class="ap-chip"><v-icon x-small>mdi-tag-outline</v-icon>{{ app.natureOfApplication }}</span>
            <span class="ap-chip"><v-icon x-small>mdi-calendar-blank-outline</v-icon>Applied {{ formatDate(app.created_at) }}</span>
            <span v-if="app.isExtended && app.originalApplication" class="ap-chip">
              <v-icon x-small>mdi-calendar-expand-horizontal</v-icon>Extension of No. {{ app.originalApplication.applicationNo }}
            </span>
          </div>
        </div>
        <div class="ap-hero-actions">
          <button type="button" class="ap-btn ap-btn--glass" @click="$router.push({ path: '/trackapplication', query: { no: app.applicationNo } })">
            <v-icon small>mdi-map-marker-path</v-icon>
            Track
          </button>
          <button type="button" class="ap-btn ap-btn--glass" @click="$router.go(-1)">
            <v-icon small>mdi-arrow-left</v-icon>
            Back
          </button>
        </div>
        <ol v-if="app.status === 'Active'" class="ap-track ap-track--dark detail-track">
          <li
            v-for="(s, i) in steps"
            :key="s.key"
            class="ap-track-step"
            :class="{ 'is-done': i < done, 'is-current': i === done }"
          >
            <span class="ap-track-dot"><v-icon v-if="i < done" x-small>mdi-check</v-icon></span>
            {{ s.label }}
          </li>
        </ol>
      </section>

      <!-- Status notices -->
      <div v-if="app.status === 'Rejected'" class="ap-notice ap-notice--danger mb-4">
        <v-icon>mdi-close-octagon-outline</v-icon>
        <span>
          <strong>Application rejected.</strong>
          <template v-if="app.rejectReasondfo && app.rejectReasondfo !== 'Nill'"> Reason: {{ app.rejectReasondfo }}</template>
        </span>
      </div>
      <div v-else-if="app.status === 'Cancelled'" class="ap-notice ap-notice--warning mb-4">
        <v-icon>mdi-cancel</v-icon>
        <span><strong>This application was cancelled.</strong> You can download the cancellation order below.</span>
      </div>
      <div v-else-if="app.status === 'Cancelrequested' || app.cancelRequest === 'Yes'" class="ap-notice ap-notice--warning mb-4">
        <v-icon>mdi-timer-sand</v-icon>
        <span><strong>Cancellation requested.</strong> The division office will review your request.</span>
      </div>
      <div v-else-if="app.task === 'T0'" class="ap-notice ap-notice--info mb-4">
        <v-icon>mdi-reply-outline</v-icon>
        <span>
          <strong>Returned to you for changes.</strong>
          <template v-if="app.reason && app.reason !== 'Nill'"> Remarks: {{ app.reason }}</template>
          <button type="button" class="ap-btn ap-btn--primary ap-btn--sm ml-2" @click="$router.push('/editbooking?id=' + app._id)">Edit &amp; resubmit</button>
        </span>
      </div>

      <div class="detail-layout">
        <div class="detail-main">
          <!-- Pay now -->
          <section v-if="canPay(app)" class="ap-card pay-card">
            <div class="ap-card-body pay-body">
              <div>
                <span class="ap-kicker pay-kicker">Approved · action needed</span>
                <h2 class="pay-title">Pay ₹{{ money(total) }} to continue</h2>
                <p class="pay-sub">Shooting fee ₹{{ money(app.shootingfee) }} + installation fee ₹{{ money(app.installationfee) }}, paid securely through the Kerala e-Treasury.</p>
              </div>
              <button type="button" class="ap-btn ap-btn--primary" @click="openPayment">
                <v-icon small>mdi-bank-outline</v-icon>
                Pay fees
              </button>
            </div>
          </section>

          <!-- Locations -->
          <section class="ap-card">
            <div class="ap-card-head">
              <div class="ap-card-heading">
                <span class="ap-card-icon"><v-icon small>mdi-map-marker-multiple-outline</v-icon></span>
                <div>
                  <h2 class="ap-card-title">Shooting schedule</h2>
                  <p class="ap-card-sub">{{ locations.length }} location{{ locations.length === 1 ? "" : "s" }}</p>
                </div>
              </div>
            </div>
            <div class="ap-card-body">
              <div v-for="(loc, i) in locations" :key="i" class="loc-row">
                <span class="loc-index">{{ String(i + 1).padStart(2, "0") }}</span>
                <div class="loc-main">
                  <div class="loc-name">{{ loc.location }}</div>
                  <div class="loc-film" v-if="loc.filmName">{{ loc.filmName }}</div>
                </div>
                <div class="loc-facts">
                  <span><v-icon x-small>mdi-calendar-range</v-icon>{{ formatDate(loc.fromDate) }} – {{ formatDate(loc.toDate) }}</span>
                  <span><v-icon x-small>mdi-clock-outline</v-icon>{{ loc.shootingDuration }} day{{ loc.shootingDuration == 1 ? "" : "s" }}<template v-if="loc.installationDays"> + {{ loc.installationDays }} setup</template></span>
                  <span><v-icon x-small>mdi-account-group-outline</v-icon>{{ loc.noOfMembers }} crew</span>
                  <span><v-icon x-small>mdi-car-outline</v-icon>{{ loc.noOfVehicles }} vehicles</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Applicant -->
          <section class="ap-card">
            <div class="ap-card-head">
              <div class="ap-card-heading">
                <span class="ap-card-icon"><v-icon small>mdi-account-outline</v-icon></span>
                <h2 class="ap-card-title">Applicant</h2>
              </div>
            </div>
            <div class="ap-card-body">
              <div class="ap-info">
                <div v-for="f in applicantFields" :key="f.label" class="ap-info-item">
                  <div class="ap-info-label">{{ f.label }}</div>
                  <div class="ap-info-value">{{ f.value || "—" }}</div>
                </div>
              </div>
            </div>
          </section>

          <!-- Documents -->
          <section class="ap-card">
            <div class="ap-card-head">
              <div class="ap-card-heading">
                <span class="ap-card-icon"><v-icon small>mdi-folder-file-outline</v-icon></span>
                <h2 class="ap-card-title">Documents</h2>
              </div>
            </div>
            <div class="ap-card-body doc-stack">
              <button type="button" class="doc-item" @click="pdf('/application/pdf')">
                <v-icon small>mdi-file-pdf-box</v-icon><span>Application form</span><v-icon x-small>mdi-download</v-icon>
              </button>
              <button type="button" class="doc-item" @click="pdf('/acknowledgement/pdf')">
                <v-icon small>mdi-file-pdf-box</v-icon><span>Acknowledgement</span><v-icon x-small>mdi-download</v-icon>
              </button>
              <button v-if="app.paymentStatus === 'Success'" type="button" class="doc-item" @click="pdf('/new/agreement/pdf')">
                <v-icon small>mdi-file-sign</v-icon><span>Agreement</span><v-icon x-small>mdi-download</v-icon>
              </button>
              <a v-if="app.proceedings" :href="fileUrl(app.proceedings)" target="_blank" rel="noopener" class="doc-item doc-item--gold">
                <v-icon small>mdi-gavel</v-icon><span>Proceedings order</span><v-icon x-small>mdi-open-in-new</v-icon>
              </a>
              <a v-if="app.license" :href="fileUrl(app.license)" target="_blank" rel="noopener" class="doc-item doc-item--gold">
                <v-icon small>mdi-file-certificate-outline</v-icon><span>Shooting licence</span><v-icon x-small>mdi-open-in-new</v-icon>
              </a>
              <button v-if="app.status === 'Rejected'" type="button" class="doc-item" @click="pdf('/rejectby/divsion/pdf')">
                <v-icon small>mdi-file-document-outline</v-icon><span>Rejection order</span><v-icon x-small>mdi-download</v-icon>
              </button>
              <button v-if="app.status === 'Cancelled'" type="button" class="doc-item" @click="pdf('/cancelby/applicant/pdf')">
                <v-icon small>mdi-file-document-outline</v-icon><span>Cancellation order</span><v-icon x-small>mdi-download</v-icon>
              </button>
              <div class="doc-divider">Uploaded by you</div>
              <a v-if="app.idProof" :href="fileUrl(app.idProof)" target="_blank" rel="noopener" class="doc-item doc-item--plain">
                <v-icon small>mdi-card-account-details-outline</v-icon><span>ID proof</span><v-icon x-small>mdi-open-in-new</v-icon>
              </a>
              <a v-if="app.animalinvolved === 'YES' && app.Preshootfile" :href="fileUrl(app.Preshootfile)" target="_blank" rel="noopener" class="doc-item doc-item--plain">
                <v-icon small>mdi-paw</v-icon><span>Pre-shoot order</span><v-icon x-small>mdi-open-in-new</v-icon>
              </a>
              <a v-if="app.additionalProof" :href="fileUrl(app.additionalProof)" target="_blank" rel="noopener" class="doc-item doc-item--plain">
                <v-icon small>mdi-paperclip</v-icon><span>Additional document</span><v-icon x-small>mdi-open-in-new</v-icon>
              </a>
            </div>
          </section>

          <!-- Animals -->
          <section v-if="app.animalinvolved === 'YES'" class="ap-card">
            <div class="ap-card-head">
              <div class="ap-card-heading">
                <span class="ap-card-icon"><v-icon small>mdi-paw</v-icon></span>
                <h2 class="ap-card-title">Animals involved</h2>
              </div>
            </div>
            <div class="ap-card-body">
              <div class="animal-chips">
                <span v-for="(a, i) in animalList" :key="i" class="animal-chip">
                  <v-icon x-small>mdi-paw</v-icon>
                  {{ a.species }} <b>× {{ a.count }}</b>
                </span>
              </div>
              <div class="ap-info">
                <div class="ap-info-item"><div class="ap-info-label">Total animals</div><div class="ap-info-value">{{ app.noOfAnimal || "—" }}</div></div>
                <div class="ap-info-item"><div class="ap-info-label">Pre-shoot order</div><div class="ap-info-value">{{ app.preshootNumber || "—" }}</div></div>
                <div class="ap-info-item"><div class="ap-info-label">Valid</div><div class="ap-info-value">{{ formatDate(app.validityfromDate) }} – {{ formatDate(app.validitytoDate) }}</div></div>
              </div>
            </div>
          </section>

          <!-- Payments -->
          <section v-if="payments.length" class="ap-card">
            <div class="ap-card-head">
              <div class="ap-card-heading">
                <span class="ap-card-icon"><v-icon small>mdi-receipt-text-outline</v-icon></span>
                <h2 class="ap-card-title">Payment history</h2>
              </div>
            </div>
            <div class="ap-card-body">
              <div v-for="(txn, i) in payments" :key="i" class="pay-row">
                <div>
                  <div class="pay-row-amount">₹{{ money(txn.totalAmount || txn.AMOUNT) }}</div>
                  <div class="pay-row-meta">{{ formatDate(txn.paymentInitiateDate) }} · GRN {{ txn.GRN || "—" }}</div>
                </div>
                <span class="ap-pill" :class="txn.status === 'Success' ? 'ap-pill--success' : 'ap-pill--warning'">{{ txn.status || "Pending" }}</span>
              </div>
            </div>
          </section>
        </div>

        <aside class="detail-side">
          <!-- Status & fees -->
          <section class="ap-card fee-card">
            <div class="fee-head">
              <span class="fee-head-label">Status</span>
              <span class="ap-pill" :class="'ap-pill--' + statusOf(app).tone">{{ statusOf(app).label }}</span>
            </div>
            <div v-if="licensePendingCancel" class="fee-flag">
              <v-icon x-small>mdi-timer-sand</v-icon>
              Cancellation requested · awaiting the division office
            </div>

            <div class="fee-lines">
              <div class="fee-line">
                <span class="fee-line-label"><v-icon x-small>mdi-movie-open-outline</v-icon>Shooting fee</span>
                <b class="fee-amount">₹{{ money(app.shootingfee) }}</b>
              </div>
              <div class="fee-line">
                <span class="fee-line-label"><v-icon x-small>mdi-tools</v-icon>Installation fee</span>
                <b class="fee-amount">₹{{ money(app.installationfee) }}</b>
              </div>
            </div>

            <div class="fee-total">
              <div class="fee-total-text">
                <div class="fee-total-label">Total fees</div>
                <span class="ap-pill" :class="'ap-pill--' + payState.tone">{{ payState.label }}</span>
              </div>
              <div class="fee-total-amount">₹{{ money(total) }}</div>
            </div>

            <div v-if="app.refundAmount || (app.status === 'Cancelled' && app.paymentStatus === 'Success')" class="fee-lines fee-lines--refund">
              <div v-if="app.refundAmount" class="fee-line">
                <span class="fee-line-label"><v-icon x-small>mdi-cash-refund</v-icon>Refund amount</span>
                <b class="fee-amount">₹{{ money(app.refundAmount) }}</b>
              </div>
              <div v-if="app.status === 'Cancelled' && app.paymentStatus === 'Success'" class="fee-line">
                <span class="fee-line-label"><v-icon x-small>mdi-progress-clock</v-icon>Refund status</span>
                <b>{{ { Pending: "To be processed", Requested: "Requested", Completed: "Completed", Failed: "Being re-checked" }[app.refundStatus] || "To be processed" }}</b>
              </div>
            </div>
          </section>

          <!-- Cancel -->
          <section v-if="canCancel" class="ap-card">
            <div class="ap-card-body cancel-body">
              <div>
                <div class="cancel-title">Need to cancel?</div>
                <div class="cancel-sub">The division office reviews cancellation requests.</div>
              </div>
              <button type="button" class="ap-btn ap-btn--danger ap-btn--sm" @click="reason = ''; cancelDialog = true">
                <v-icon small>mdi-cancel</v-icon>
                Request cancellation
              </button>
            </div>
          </section>
        </aside>
      </div>
    </template>

    <!-- Payment -->
    <v-dialog v-model="payDialog" max-width="640" persistent>
      <v-card class="ap-dialog">
        <div class="ap-dialog-head">
          <h2 class="ap-dialog-title">Pay ₹{{ money(total) }}</h2>
          <p class="ap-dialog-sub">Enter the payer details for the e-Treasury challan. The payment page opens in a new tab.</p>
        </div>
        <div class="ap-page ap-dialog-body">
          <div class="ap-grid ap-grid--2">
            <v-text-field v-model="payer.name" outlined dense hide-details="auto" label="Payer name *" class="ap-span-2"></v-text-field>
            <v-text-field v-model="payer.address" outlined dense hide-details="auto" label="Address *" class="ap-span-2"></v-text-field>
            <v-text-field v-model="payer.phone" outlined dense hide-details="auto" label="Mobile number *" maxlength="10"></v-text-field>
            <v-text-field v-model="payer.pincode" outlined dense hide-details="auto" label="Pincode *" maxlength="6"></v-text-field>
            <v-text-field v-model="payer.pan" outlined dense hide-details="auto" label="PAN number *" maxlength="10" class="ap-span-2"></v-text-field>
          </div>
        </div>
        <div class="ap-dialog-actions">
          <button type="button" class="ap-btn ap-btn--outline" @click="payDialog = false">Cancel</button>
          <button type="button" class="ap-btn ap-btn--primary" :disabled="paying" @click="submitPayment">
            <v-icon small>mdi-lock-outline</v-icon>
            Continue to e-Treasury
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Cancel -->
    <v-dialog v-model="cancelDialog" max-width="520" persistent>
      <v-card class="ap-dialog">
        <div class="ap-dialog-head ap-dialog-head--danger">
          <h2 class="ap-dialog-title">Request cancellation</h2>
          <p class="ap-dialog-sub">Application No. {{ app && app.applicationNo }}</p>
        </div>
        <div class="ap-page ap-dialog-body">
          <v-textarea v-model="reason" outlined dense rows="3" auto-grow hide-details label="Reason for cancellation *"></v-textarea>
        </div>
        <div class="ap-dialog-actions">
          <button type="button" class="ap-btn ap-btn--outline" @click="cancelDialog = false">Keep application</button>
          <button type="button" class="ap-btn ap-btn--danger-solid" :disabled="busy" @click="submitCancel">Request cancellation</button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { APPLICANT_STEPS, stepsDone, nextStepText, canPay, statusOf, isLicensed, openPdf } from "@/components/Common/applicantConfig";

// e-Treasury challan page the payment form is posted to.
const ETREASURY_URL = "https://etreasury.kerala.gov.in/api/eTreasury/service/ChallanGeneration.php";

// Applicant's view of one application: progress, schedule, documents, fee
// payment and cancellation.
export default {
  name: "ApplicantApplicationDetail",
  data() {
    return {
      id: this.$route.query.id,
      app: null,
      loading: true,
      busy: false,
      paying: false,
      msg: "",
      showSnackBar: false,
      payDialog: false,
      cancelDialog: false,
      reason: "",
      payer: { name: "", address: "", phone: "", pincode: "", pan: "" },
      steps: APPLICANT_STEPS,
    };
  },
  computed: {
    done() {
      return stepsDone(this.app);
    },
    total() {
      return (this.app && (Number(this.app.shootingfee) || 0) + (Number(this.app.installationfee) || 0)) || 0;
    },
    locations() {
      return (this.app && this.app.filmShootingDetails) || [];
    },
    payments() {
      return (this.app && this.app.paymentTransactions) || [];
    },
    filmName() {
      const first = this.locations[0];
      return (first && first.filmName) || (this.app && this.app.natureOfApplication) || "Shooting application";
    },
    // Each animal in the shoot; older applications only have one species.
    animalList() {
      const a = this.app || {};
      if (a.animals && a.animals.length) return a.animals;
      return a.species ? [{ species: a.species, count: a.noOfAnimal }] : [];
    },
    canCancel() {
      return !!this.app && this.app.status === "Active" && this.app.cancelRequest === "No";
    },
    // A licensed application whose cancellation the division office has not decided yet
    licensePendingCancel() {
      return !!this.app && isLicensed(this.app) && (this.app.status === "Cancelrequested" || this.app.cancelRequest === "Yes");
    },
    // Whether the fees are paid, due or not asked for yet
    payState() {
      const a = this.app || {};
      if (a.paymentStatus === "Success") return { label: "Paid", tone: "success" };
      if (a.paymentInitiation === "YES") return { label: "Payment due", tone: "warning" };
      return { label: "Not yet due", tone: "neutral" };
    },
    applicantFields() {
      const a = this.app || {};
      return [
        { label: "Name", value: a.applicantName },
        { label: "Mobile", value: a.applicantMobileNo },
        { label: "Email", value: a.applicantEmail },
        { label: "District", value: a.applicantDistrict },
        { label: "Address", value: a.applicantAddress },
        { label: "Pincode", value: a.applicantPinCode },
        { label: "ID type", value: a.idType },
        { label: "ID number", value: a.idcardNo },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    statusOf,
    nextStepText,
    canPay,
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    headers() {
      return { token: localStorage.getItem("token") };
    },
    load() {
      if (!this.id) {
        this.loading = false;
        return;
      }
      axios({ method: "POST", url: "/single/application", headers: this.headers(), data: { appid: this.id } })
        .then((response) => {
          this.loading = false;
          if (response.data.status) this.app = response.data.data;
          else this.toast(response.data.msg);
        })
        .catch((err) => {
          this.loading = false;
          console.log(err);
        });
    },
    fileUrl(key) {
      return this.baseURL + "file/get/" + key;
    },
    pdf(url) {
      openPdf(axios, url, this.id).catch(() => this.toast("Could not open the document."));
    },

    // ── Payment ──
    openPayment() {
      this.payer = { name: this.app.applicantName || "", address: this.app.applicantAddress || "", phone: String(this.app.applicantMobileNo || ""), pincode: this.app.applicantPinCode || "", pan: "" };
      this.payDialog = true;
    },
    submitPayment() {
      const p = this.payer;
      if (!p.name || !p.address || !p.phone || !p.pincode || !p.pan) {
        this.toast("Please fill in all the payer details.");
        return;
      }
      if (!/^[0-9]{10}$/.test(p.phone)) return this.toast("Mobile number must be 10 digits.");
      if (!/^[0-9]{6}$/.test(p.pincode)) return this.toast("Pincode must be 6 digits.");
      if (!/^[A-Za-z0-9]{10}$/.test(p.pan)) return this.toast("PAN number must have exactly 10 characters.");
      this.paying = true;
      axios({
        url: "/fees/submission",
        method: "POST",
        headers: this.headers(),
        data: {
          appid: this.id,
          address: p.address,
          mobnumber: p.phone,
          pin_no: p.pincode,
          pan_no: p.pan,
          party_name: p.name,
          amount: this.total,
        },
      })
        .then((response) => {
          if (!response.data.status) {
            this.paying = false;
            this.toast(response.data.msg);
            return;
          }
          this.payDialog = false;
          this.postToTreasury(response.data.data);
          this.notifyDivision();
        })
        .catch((err) => {
          this.paying = false;
          this.toast("Could not start the payment. Please try again.");
          console.log(err);
        });
    },
    // Opens the e-Treasury challan page in a new tab with the signed request.
    postToTreasury(details) {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = ETREASURY_URL;
      form.target = "_blank";
      const add = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = JSON.stringify(value);
        form.appendChild(input);
      };
      add("input_data", { HMAC: details.HMAC, DATA: details.DATA });
      add("input_headers", { clientId: details.clientId, authToken: details.authToken });
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    // Same as before the redesign: once the challan is generated the
    // application moves back to the division office (/check/forward).
    notifyDivision() {
      axios({ url: "/check/forward", method: "POST", headers: this.headers(), data: { appid: this.id } })
        .then((response) => {
          this.paying = false;
          this.toast(response.data.msg);
          this.load();
        })
        .catch((err) => {
          this.paying = false;
          console.log(err);
        });
    },

    // ── Cancel ──
    submitCancel() {
      if (!this.reason || !this.reason.trim()) {
        this.toast("Please enter the reason for cancellation.");
        return;
      }
      this.busy = true;
      axios({ url: "/cancel/application", method: "POST", headers: this.headers(), data: { appid: this.id, reason: this.reason.trim() } })
        .then((response) => {
          this.busy = false;
          if (!response.data.status) {
            this.toast(response.data.msg);
            return;
          }
          this.cancelDialog = false;
          this.$appAlert({ type: "success", title: "Cancellation requested", message: response.data.msg }).then(() => this.load());
        })
        .catch((err) => {
          this.busy = false;
          this.toast("Could not send the cancellation request.");
          console.log(err);
        });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "—";
    },
  },
};
</script>

<style scoped>
/* Compact banner: less height means less scrolling before the details */
.detail-hero {
  align-items: center;
  gap: 10px 16px;
  padding: 16px clamp(18px, 2.4vw, 28px) 14px;
  margin-bottom: 16px;
  border-radius: 22px;
}

.detail-hero .ap-kicker {
  margin-bottom: 4px;
}

.detail-hero .ap-hero-title {
  margin-bottom: 2px;
  font-size: clamp(22px, 2.4vw, 30px);
}

.detail-hero .ap-hero-sub {
  font-size: 14px;
  line-height: 1.45;
}

.detail-hero .ap-hero-meta {
  gap: 6px;
  margin-top: 8px;
}

.detail-hero .ap-chip {
  padding: 3px 10px;
  font-size: 12px;
}

.detail-hero .ap-btn {
  min-height: 36px;
  padding: 0 16px;
  font-size: 13px;
}

.detail-track {
  flex-basis: 100%;
  margin-top: 0;
  padding-top: 12px;
  border-top: 1px solid rgba(233, 245, 238, 0.12);
}

/* Dot above its label, with the connector line running between the dots */
.detail-track .ap-track-step {
  flex-direction: column;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
}

.detail-track .ap-track-dot {
  width: 22px;
  height: 22px;
}

.detail-track .ap-track-step::before {
  top: 10px;
}

.detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 18px;
  align-items: start;
}

/* Status and fees stay in view while the details scroll */
.detail-side {
  position: sticky;
  top: 84px;
}

.pay-card {
  border-color: rgba(22, 163, 74, 0.35);
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%);
}

.pay-body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 22px;
}

.pay-kicker {
  color: #15803d;
  margin-bottom: 6px;
}

.pay-kicker::before {
  background: #15803d;
}

.pay-title {
  margin: 0 0 4px;
  font-family: "Inter", sans-serif;
  font-size: 26px;
  font-weight: 500;
  color: var(--ap-ink);
}

.pay-sub {
  margin: 0;
  font-size: 13.5px;
  color: var(--ap-muted);
}

.loc-row {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 4px 14px;
  padding: 16px 0;
  border-bottom: 1px solid var(--ap-line);
}

.loc-row:first-child {
  padding-top: 4px;
}

.loc-row:last-child {
  border-bottom: none;
}

.loc-index {
  grid-row: span 2;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  color: var(--ap-gold);
}

.loc-name {
  font-size: 16px;
  font-weight: 600;
}

.loc-film {
  font-size: 13px;
  color: var(--ap-muted);
}

.loc-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: 13px;
  color: #33463d;
}

.loc-facts .v-icon {
  margin-right: 4px;
  color: #15803d !important;
}

.animal-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.animal-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  background: var(--ap-emerald-tint);
  font-size: 14px;
  font-weight: 500;
  color: #14532d;
}

.animal-chip .v-icon {
  color: inherit !important;
}

.pay-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--ap-line);
}

.pay-row:last-child {
  border-bottom: none;
}

.pay-row-amount {
  font-family: "Inter", sans-serif;
  font-size: 19px;
}

.pay-row-meta {
  font-size: 12.5px;
  color: var(--ap-muted);
}

/* Status and fees */
.fee-card {
  overflow: hidden;
}

.fee-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--ap-line);
  background: linear-gradient(180deg, #fbfdfc 0%, #ffffff 100%);
}

.fee-head-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--ap-muted);
}

.fee-flag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
  font-size: 12.5px;
  font-weight: 600;
  color: #92400e;
}

.fee-flag .v-icon {
  color: inherit !important;
}

.fee-lines {
  padding: 6px 22px;
}

.fee-lines--refund {
  border-top: 1px dashed var(--ap-line);
  padding-top: 8px;
  padding-bottom: 10px;
}

.fee-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px dashed var(--ap-line);
  font-size: 14px;
}

.fee-line:last-child {
  border-bottom: none;
}

.fee-line-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ap-muted);
}

.fee-line-label .v-icon {
  color: #94a8a0 !important;
}

.fee-line b {
  font-weight: 600;
  color: var(--ap-ink);
}

.fee-amount {
  font-variant-numeric: lining-nums tabular-nums;
}

/* The total stands out: tinted band, large level figure */
.fee-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 4px 14px 16px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, #ecfdf3 0%, #f7fdf9 100%);
  border: 1px solid #c8efd6;
}

.fee-total-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.fee-total-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #3f6b52;
}

.fee-total-amount {
  font-family: "Inter", sans-serif;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.1;
  color: #14532d;
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
}

.doc-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.doc-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 14px;
  border-radius: 14px;
  border: 1px solid var(--ap-line);
  background: #ffffff;
  color: var(--ap-ink) !important;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.doc-item span {
  flex: 1;
}

.doc-item .v-icon {
  color: #15803d !important;
}

.doc-item:hover {
  border-color: rgba(22, 163, 74, 0.4);
  background: #f6fbf8;
}

.doc-item--gold {
  border-color: rgba(184, 149, 90, 0.45);
  background: #fdfbf6;
}

.doc-item--gold .v-icon {
  color: #8a6a32 !important;
}

.doc-item--plain {
  background: #fbfcfb;
}

.doc-divider {
  margin: 8px 0 2px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--ap-muted);
}

.cancel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
}

.cancel-title {
  font-size: 15px;
  font-weight: 600;
}

.cancel-sub {
  font-size: 13px;
  color: var(--ap-muted);
}

@media (max-width: 1100px) {
  .detail-layout {
    grid-template-columns: 1fr;
  }

  .detail-side {
    position: static;
  }
}
</style>
